/**
 * Arka planda konum (9.3.3.7 · D-588).
 *
 * Bugüne kadar ölçüm bir JS zamanlayıcısıyla dönüyordu (9.3.3.3). Zamanlayıcı
 * uygulama öne çıkmadıkça çalışmaz: yani canlı paylaşım, telefon cebe girdiği
 * anda sessizce duruyordu — tam da paylaşımın en gerekli olduğu an.
 *
 * Google Maps ve Find My burada zamanlayıcı kullanmıyor; işletim sisteminden
 * "anlamlı hareket oldu" uyandırması alıyorlar ve aralarda uygulama ASKIDA
 * duruyor. Bu dosya aynı deseni kuruyor: mesafe eşiği veriliyor, uyandırmayı
 * işletim sistemi yapıyor.
 *
 * `expo-task-manager` yeni bir bağımlılık. D-556 Expo'yu varsayılan saymıyor;
 * burada gerekçe şu: arka plan konumu `expo-location`'ın KENDİ arka plan
 * API'siyle geliyor ve o API TaskManager olmadan çalışmıyor. Expo dışı bir yol
 * seçmek, çalışan `expo-location`'ı tümüyle değiştirmek demekti — kaçış
 * kapısının öngördüğünden çok daha büyük bir iş.
 */
import { decideSend, MIN_SEND_DISTANCE_M, type Point } from './locationEnergy';

export const LOCATION_TASK = 'nosubject-live-location';

type LocationModule = typeof import('expo-location');
type TaskModule = typeof import('expo-task-manager');

function yukleLocation(): LocationModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-location') as LocationModule;
  } catch {
    return null;
  }
}

function yukleTask(): TaskModule | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    return require('expo-task-manager') as TaskModule;
  } catch {
    return null;
  }
}

export const backgroundLocationAvailable = yukleLocation() != null && yukleTask() != null;

/**
 * Arka plan izni ayrı bir izindir ve ön plan izninden SONRA istenir.
 *
 * iOS önce "kullanırken", ancak sonra "her zaman" sorar; ikisini birden
 * istemek sistem tarafından reddedilir. Reddedilirse paylaşım yine çalışır,
 * yalnız uygulama öndeyken — yani bugünkü davranış.
 */
export async function requestBackgroundLocationPermission(): Promise<boolean> {
  const Location = yukleLocation();
  if (!Location) return false;
  try {
    const on = await Location.requestForegroundPermissionsAsync();
    if (!on.granted) return false;
    const arka = await Location.requestBackgroundPermissionsAsync();
    return arka.granted;
  } catch {
    return false;
  }
}

/**
 * Arka plan güncellemelerini başlatır.
 *
 * `distanceInterval` eşiği, 9.3.3.3'teki gönderme eşiğiyle AYNI sayıdan
 * geliyor: iki ayrı sabit, birbirinden habersiz sürüklenirdi.
 */
export async function startBackgroundLocation(): Promise<boolean> {
  const Location = yukleLocation();
  const TaskManager = yukleTask();
  if (!Location || !TaskManager) return false;
  try {
    if (!TaskManager.isTaskDefined(LOCATION_TASK)) return false;
    const acik = await Location.hasStartedLocationUpdatesAsync(LOCATION_TASK);
    if (acik) return true;
    await Location.startLocationUpdatesAsync(LOCATION_TASK, {
      accuracy: Location.Accuracy.Balanced,
      distanceInterval: MIN_SEND_DISTANCE_M,
      // Duran telefonu boşuna uyandırmamak için: işletim sistemi bu süre ve
      // mesafe dolana kadar güncellemeleri biriktirebilir.
      deferredUpdatesDistance: MIN_SEND_DISTANCE_M,
      deferredUpdatesInterval: 60_000,
      pausesUpdatesAutomatically: true,
      showsBackgroundLocationIndicator: true,
      foregroundService: {
        notificationTitle: 'nosubject',
        notificationBody: 'Canlı konum paylaşılıyor',
      },
    });
    return true;
  } catch {
    return false;
  }
}

export async function stopBackgroundLocation(): Promise<void> {
  const Location = yukleLocation();
  if (!Location) return;
  try {
    if (await Location.hasStartedLocationUpdatesAsync(LOCATION_TASK)) {
      await Location.stopLocationUpdatesAsync(LOCATION_TASK);
    }
  } catch {
    // Zaten durmuş olabilir; durdurmanın başarısızlığı paylaşımı bozmuyor.
  }
}

/**
 * İşletim sisteminin verdiği noktalardan hangisinin gönderileceği.
 *
 * Kural ön plandakiyle AYNI (9.3.3.3): hatalı ölçüm gitmez, anlamlı hareket
 * yoksa gitmez, ama nabız atar. İki yerde iki ayrı kural, aynı paylaşımın
 * öne/arkaya geçmesiyle davranış değiştirmesi demekti.
 */
export function backgroundDecision(input: {
  current: Point;
  lastSent: Point | null;
  secondsSinceLastSend: number;
}) {
  return decideSend(input);
}
